import React, { useEffect, useState } from "react";
import { MediaUrl } from "../shared/types";

// ----------------------------  MediaGallery  ----------------------------
// Shows every image from a sample's S3 folder: a large selected image with
// previous / next controls and a clickable thumbnail strip. Non-image media
// render as links below the images.

interface MediaGalleryProps {
  media: MediaUrl[];
}

const MediaGallery: React.FC<MediaGalleryProps> = ({ media }) => {
  const images = media.filter((m) => m.type === "image" && !!m.url);
  const others = media.filter((m) => !(m.type === "image" && !!m.url));
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<string, boolean>>({});

  // A new task brings a new image set; start again from the first image.
  useEffect(() => {
    setIndex(0);
    setFailed({});
  }, [media]);

  const count = images.length;
  const current = count > 0 ? images[Math.min(index, count - 1)] : null;
  const go = (delta: number) => setIndex((i) => (i + delta + count) % count);
  const markFailed = (url: string) => setFailed((f) => ({ ...f, [url]: true }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {current && (
        <>
          {failed[current.url] ? (
            <div style={brokenBox}>
              Image failed to load.{" "}
              <a href={current.url} target="_blank" rel="noopener noreferrer">
                Open in new tab
              </a>
            </div>
          ) : (
            <a href={current.url} target="_blank" rel="noopener noreferrer" style={mainLink}>
              <img
                src={current.url}
                alt={current.label || "image " + (index + 1)}
                style={mainImage}
                onError={() => markFailed(current.url)}
              />
            </a>
          )}

          {/* -- Caption + navigation -- */}
          <div style={navRow}>
            {count > 1 && (
              <button
                type="button"
                style={navBtn}
                onClick={() => go(-1)}
                aria-label="Previous image"
              >
                &lsaquo; Prev
              </button>
            )}
            <div style={caption} title={current.label}>
              {current.label || "Image"}
              {count > 1 && (
                <span style={{ color: "#9ca3af", marginLeft: "8px" }}>
                  {Math.min(index, count - 1) + 1} / {count}
                </span>
              )}
            </div>
            {count > 1 && (
              <button type="button" style={navBtn} onClick={() => go(1)} aria-label="Next image">
                Next &rsaquo;
              </button>
            )}
          </div>

          {/* -- Thumbnails -- */}
          {count > 1 && (
            <div style={thumbGrid}>
              {images.map((m, i) => {
                const selected = i === Math.min(index, count - 1);
                return (
                  <button
                    key={m.url}
                    type="button"
                    onClick={() => setIndex(i)}
                    title={m.label}
                    aria-label={"Show " + (m.label || "image " + (i + 1))}
                    style={{ ...thumbBtn, ...(selected ? thumbSelected : {}) }}
                  >
                    {failed[m.url] ? (
                      <span style={thumbBroken}>!</span>
                    ) : (
                      <img
                        src={m.url}
                        alt=""
                        loading="lazy"
                        style={thumbImage}
                        onError={() => markFailed(m.url)}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {others.map((m, i) => (
        <div key={i} style={brokenBox}>
          <span style={{ color: "#9ca3af", marginRight: "8px" }}>{m.type}</span>
          {m.url ? (
            <a href={m.url} target="_blank" rel="noopener noreferrer">
              {m.label || "Open"}
            </a>
          ) : (
            m.label
          )}
        </div>
      ))}
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const mainLink: React.CSSProperties = {
  display: "block",
  width: "100%",
};

const mainImage: React.CSSProperties = {
  width: "100%",
  height: "auto",
  maxHeight: "70vh",
  objectFit: "contain",
  borderRadius: "8px",
  background: "#f3f4f6",
  display: "block",
};

const navRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const navBtn: React.CSSProperties = {
  border: "1px solid #d1d5db",
  background: "white",
  borderRadius: "6px",
  padding: "6px 12px",
  fontSize: "1rem",
  color: "#374151",
  cursor: "pointer",
  flexShrink: 0,
};

const caption: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  textAlign: "center",
  fontSize: "1rem",
  color: "#374151",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const thumbGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
  gap: "6px",
  maxHeight: "240px",
  overflowY: "auto",
};

const thumbBtn: React.CSSProperties = {
  padding: 0,
  border: "2px solid transparent",
  borderRadius: "6px",
  background: "#f3f4f6",
  cursor: "pointer",
  aspectRatio: "1 / 1",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const thumbSelected: React.CSSProperties = {
  borderColor: "#0f7b8a",
};

const thumbImage: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const thumbBroken: React.CSSProperties = {
  color: "#ef4444",
  fontWeight: 700,
};

const brokenBox: React.CSSProperties = {
  background: "#f3f4f6",
  borderRadius: "8px",
  padding: "16px",
  textAlign: "center",
  fontSize: "1rem",
  color: "#6b7280",
};

export default MediaGallery;
